import { useClientPortalAPI } from "@certn/api";
import {
  Divider,
  Link,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { SkeletonGrid } from "@certn/cascadia-react/patterns/skeleton-grid/SkeletonGrid";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useSWRConfig } from "swr";

import { DashboardCard } from "../../components/dashboard-main/DashboardCard";
import { TaskRow } from "../../components/tasks/TaskRow";
import { TasksEmptyState } from "../../components/tasks/TasksEmptyState";
import { Schemas } from "../../types";

interface DashboardTaskProps extends PropsWithChildren {
  title: string;
  testID?: string;
  className?: string;
  onRowPress: (task: Schemas["LoggedInUserTaskList"]) => void;
}

export function DashboardTasks(props: DashboardTaskProps) {
  const { title, testID, className, onRowPress } = props;
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const {
    data,
    isLoading,
    mutate: mutateMeTasks,
  } = useClientPortalAPI("/api/internal/client-portal/me/tasks/");

  const activeList = data?.results?.filter((task) => {
    return task.status === "ACTIVE";
  });
  const completeList = data?.results?.filter((task) => {
    return task.status === "COMPLETE";
  });

  const updateTaskData = () => {
    mutateMeTasks();
    mutate("/api/internal/client-portal/me/tasks/?status=ACTIVE");
  };

  const trailingViewAllLink = (
    <Link
      textStyle="form/body-secondary-link"
      textColor="text-weak"
      testID="view-all-tasks-link"
      href="/tasks"
    >
      {t("View all")}
    </Link>
  );

  return (
    <DashboardCard
      title={isLoading ? <Skeleton width="100px" /> : title}
      testID={testID}
      trailing={isLoading ? <Skeleton width="100px" /> : trailingViewAllLink}
      className={className}
    >
      {isLoading ? (
        <SkeletonGrid />
      ) : (
        <Tabs testID="dashboard-tasks-tabs" gap="space-3">
          <TabList aria-label={t("Status of my tasks")}>
            <Tab id="active-tasks" testID="dashboard-tasks-active-tasks-tab">
              {t("Active")}
            </Tab>
            <Tab id="done-tasks" testID="dashboard-tasks-done-tasks-tab">
              {t("Done")}
            </Tab>
          </TabList>
          <TabPanel
            id="active-tasks"
            aria-label="dashboard-tasks-active-tasks-tab-content"
          >
            <>
              {activeList && activeList.length > 0 ? (
                activeList.map((item, index) => {
                  return (
                    <div key={index}>
                      <TaskRow
                        isLoading={isLoading}
                        task={item}
                        onRowPress={onRowPress}
                        mutate={updateTaskData}
                      />
                      <Divider />
                    </div>
                  );
                })
              ) : (
                <TasksEmptyState isSearching={false} tab="ACTIVE" />
              )}
            </>
          </TabPanel>
          <TabPanel id="done-tasks" aria-label="dashboard-tasks-done-tasks-tab">
            <>
              {completeList && completeList.length > 0 ? (
                completeList.map((item, index) => {
                  return (
                    <div key={index}>
                      <TaskRow
                        isLoading={isLoading}
                        task={item}
                        onRowPress={onRowPress}
                      />
                      <Divider />
                    </div>
                  );
                })
              ) : (
                <TasksEmptyState isSearching={false} tab="COMPLETE" />
              )}
            </>
          </TabPanel>
        </Tabs>
      )}
    </DashboardCard>
  );
}
